import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, CheckCircle2, AlertTriangle, XCircle, Target, TrendingDown, ArrowRight, Info, Sparkles, ListChecks, ClipboardList, Filter as FilterIcon } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { Select } from '../../../../../components/ui/Select';
import { MODULE_CONFIG } from '../shared';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { AssessmentPoolHealthReport, AssessmentPoolSliceFinding, AssessmentPoolFeasibilityFinding } from '../hooks/useGovernanceData';
interface AssessmentPoolHealthTabProps {
  report: AssessmentPoolHealthReport;
  isRunning: boolean;
  onSelectQuestion: (questionId: string) => void;
}
type StatusKind = 'ok' | 'degraded' | 'shortfall' | 'zero' | 'unknown';
interface MatrixCell {
  group: GroupId;
  module: string;
  status: StatusKind;
  finding?: AssessmentPoolFeasibilityFinding;
}
/**
 * AssessmentPoolHealthTab — the full-page "Toetsen" tab.
 *
 * Answers one primary question prominently: "voor welke groep+module-combinatie
 * kunnen we een complete Cito-style toets samenstellen?" — and provides the
 * underlying capacity- and distribution-diagnostics as supporting evidence.
 *
 * Layout:
 *   1. Hero header with overall feasibility readiness
 *   2. KPI strip (4 cards)
 *   3. Feasibility matrix (group × module) — the centerpiece
 *   4. Two-column detail: capacity gaps | distribution issues
 *   5. Decision-hint footer
 */
export function AssessmentPoolHealthTab({
  report,
  isRunning,
  onSelectQuestion
}: AssessmentPoolHealthTabProps) {
  // ─── Derive modules present in the report ──────────────────────────────
  const allModules = useMemo(() => {
    const set = new Set<string>();
    Object.keys(report.byModule).forEach(m => set.add(m));
    report.feasibility.forEach(f => set.add(f.module));
    report.slices.forEach(s => set.add(s.module));
    return Array.from(set).sort();
  }, [report]);
  // ─── Build feasibility matrix (group × module) ─────────────────────────
  const feasibilityIndex = useMemo(() => {
    const map = new Map<string, AssessmentPoolFeasibilityFinding>();
    report.feasibility.forEach(f => map.set(`${f.group}::${f.module}`, f));
    return map;
  }, [report.feasibility]);
  const matrix: MatrixCell[][] = useMemo(() => {
    return GROUPS.map(group => allModules.map(module => {
      const finding = feasibilityIndex.get(`${group}::${module}`);
      let status: StatusKind = 'ok';
      if (!finding) {
        // No finding emitted means either OK or no module-data for that group.
        // We default to 'ok' but downgrade to 'unknown' when there's no
        // module data anywhere in the report (preserves honesty).
        status = report.byModule[module] !== undefined ? 'ok' : 'unknown';
      } else if (finding.reason === 'zero_questions') status = 'zero';else if (finding.reason === 'shortfall') status = 'shortfall';else if (finding.reason === 'degraded') status = 'degraded';
      return {
        group,
        module,
        status,
        finding
      };
    }));
  }, [allModules, feasibilityIndex, report.byModule]);
  // ─── Aggregate matrix counts ──────────────────────────────────────────
  const matrixCounts = useMemo(() => {
    let ok = 0;
    let degraded = 0;
    let shortfall = 0;
    let zero = 0;
    let unknown = 0;
    matrix.forEach(row => row.forEach(cell => {
      if (cell.status === 'ok') ok++;else if (cell.status === 'degraded') degraded++;else if (cell.status === 'shortfall') shortfall++;else if (cell.status === 'zero') zero++;else unknown++;
    }));
    const total = ok + degraded + shortfall + zero + unknown;
    const ready = ok;
    const readinessPct = total > 0 ? Math.round(ready / total * 100) : 0;
    return {
      ok,
      degraded,
      shortfall,
      zero,
      unknown,
      total,
      readinessPct
    };
  }, [matrix]);
  // ─── Filters ─────────────────────────────────────────────────────────
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const filteredSlices = useMemo(() => {
    return report.slices.filter(s => {
      if (moduleFilter !== 'all' && s.module !== moduleFilter) return false;
      return true;
    });
  }, [report.slices, moduleFilter]);
  const filteredFeasibility = useMemo(() => {
    return report.feasibility.filter(f => {
      if (moduleFilter !== 'all' && f.module !== moduleFilter) return false;
      if (groupFilter !== 'all' && String(f.group) !== groupFilter) return false;
      if (statusFilter !== 'all' && f.reason !== statusFilter) return false;
      return true;
    });
  }, [report.feasibility, moduleFilter, groupFilter, statusFilter]);
  const selectedCell = useMemo(() => {
    if (!selectedCellKey) return null;
    for (const row of matrix) {
      for (const cell of row) {
        if (`${cell.group}::${cell.module}` === selectedCellKey) return cell;
      }
    }
    return null;
  }, [matrix, selectedCellKey]);
  // ─── Loading state ────────────────────────────────────────────────────
  if (isRunning && allModules.length === 0) {
    return <div className="space-y-6" data-id="element-4948" data-mp="2d76yc4cbnx2v">
        <SkeletonHero data-id="element-4949" data-mp="2d888m6likfjj" />
        <SkeletonMatrix data-id="element-4950" data-mp="28evsylytbj2x" />
      </div>;
  }
  // ─── Empty state (no data, no run yet) ────────────────────────────────
  if (allModules.length === 0) {
    return <Card className="p-10 text-center bg-gradient-to-br from-white to-amber-50/30 border-amber-100" data-id="element-4951" data-mp="23dzq8bq762z3">
        <Layers className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-4952" data-mp="30xb0065y9mr8" />
        <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-4953" data-mp="2w36rhih1d6o1">
          Nog geen toets-data beschikbaar
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-4954" data-mp="u9akg99wqo4r">
          De assessment-pool wordt geanalyseerd zodra de validatie-pipeline
          loopt. Klik op{' '}
          <span className="font-semibold text-gray-700" data-id="element-4955" data-mp="kgl9rgm6leah">Hervalideer</span> in de
          rechterbovenhoek om een verse run te starten.
        </p>
      </Card>;
  }
  return <div className="space-y-6" data-id="element-4956" data-mp="bggeg4u9i6co">
      {/* ── 1. HERO ── */}
      <HeroHeader counts={matrixCounts} data-id="element-4957" data-mp="bj2kg01i8dss" />

      {/* ── 2. KPI STRIP ── */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.05
    }} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4958" data-mp="fjml3dladzjo">
        <KpiTile label="Klaar voor toets" value={matrixCounts.ok} suffix={`/ ${matrixCounts.total}`} color="emerald" icon={<CheckCircle2 className="w-4 h-4" data-id="element-4960" data-mp="3leqddxdvsk2z" />} hint="Groep × module combinaties die een volledige 30-vragen toets aankunnen." data-id="element-4959" data-mp="1vxcsoubm75y2" />
        <KpiTile label="Op het randje" value={matrixCounts.degraded + matrixCounts.shortfall} color="amber" icon={<Target className="w-4 h-4" data-id="element-4962" data-mp="3qci3cluytw4j" />} hint="Combinaties met tekort of onbalans — toets is nog op te leveren, maar minder representatief." data-id="element-4961" data-mp="3gcra0ni55n61" />
        <KpiTile label="Niet beschikbaar" value={matrixCounts.zero} color="red" icon={<XCircle className="w-4 h-4" data-id="element-4964" data-mp="hwonm98gkooj" />} hint="Combinaties met 0 MC-vragen — kan op dit moment geen toets opleveren." data-id="element-4963" data-mp="80lpdegq9a6v" />
        <KpiTile label="Pool-bevindingen" value={report.affectedSlices} color="indigo" icon={<Layers className="w-4 h-4" data-id="element-4966" data-mp="2jol8i9ginavz" />} hint="Aantal (module, tier-band) slices met capaciteit- of spreidingsbevindingen." data-id="element-4965" data-mp="cz6o19gbzlt7" />
      </motion.div>

      {/* ── 3. FEASIBILITY MATRIX ── */}
      <Card className="p-5" data-id="element-4967" data-mp="1stymg17nrsew">
        <div className="flex items-start justify-between gap-4 mb-4" data-id="element-4968" data-mp="1xuw6kjc8awj1">
          <div className="min-w-0" data-id="element-4969" data-mp="1xt2uupmrdm46">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4970" data-mp="22lcjm2c50g4v">
              <Sparkles className="w-4 h-4 text-amber-500" data-id="element-4971" data-mp="27kpf1bh2qvj2" />
              Toets-feasibility per groep × module
            </h3>
            <p className="text-[11px] text-gray-500 mt-1 leading-relaxed" data-id="element-4972" data-mp="19y8jaanzopzf">
              Elke cel toont of een complete 30-vragen Cito-toets opgesteld kan
              worden voor die combinatie. Klik op een cel voor details.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 flex-shrink-0" data-id="element-4973" data-mp="1nwzwzvyxw9n1">
            <LegendDot color="emerald" label="Klaar" data-id="element-4974" data-mp="1nuvgmejuti5v" />
            <LegendDot color="amber" label="Onbalans" data-id="element-4975" data-mp="2vbbewfgxd9hz" />
            <LegendDot color="orange" label="Tekort" data-id="element-4976" data-mp="2vcw4rrsao49i" />
            <LegendDot color="red" label="0 vragen" data-id="element-4977" data-mp="2zatwp2dg94gl" />
            <LegendDot color="gray" label="—" data-id="element-4978" data-mp="norzmozmqekr" />
          </div>
        </div>

        <FeasibilityMatrix modules={allModules} matrix={matrix} selectedCellKey={selectedCellKey} onSelectCell={key => setSelectedCellKey(prev => prev === key ? null : key)} data-id="element-4979" data-mp="nqw1z2hvr5ix" />

        {/* Selected cell detail panel */}
        {selectedCell && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} transition={{
        duration: 0.2
      }} className="mt-4 overflow-hidden" data-id="element-4980" data-mp="4y1yp5tmd1uj">
            <CellDetailPanel cell={selectedCell} onSelectQuestion={onSelectQuestion} onClose={() => setSelectedCellKey(null)} data-id="element-4981" data-mp="3n4yeqi18ek8r" />
          </motion.div>}

        {/* Legend (mobile) */}
        <div className="flex sm:hidden flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100" data-id="element-4982" data-mp="1lapf0dipq3sh">
          <LegendDot color="emerald" label="Klaar" data-id="element-4983" data-mp="29zbowkzp1gl5" />
          <LegendDot color="amber" label="Onbalans" data-id="element-4984" data-mp="24xmd334ncyw0" />
          <LegendDot color="orange" label="Tekort" data-id="element-4985" data-mp="2iy70ydpf5kwd" />
          <LegendDot color="red" label="0 vragen" data-id="element-4986" data-mp="2ixnfin0k8edl" />
          <LegendDot color="gray" label="—" data-id="element-4987" data-mp="2dzc62de6g5vw" />
        </div>
      </Card>

      {/* ── 4. FILTER STRIP ── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-4988" data-mp="1q9021dm1ytal">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 px-1" data-id="element-4989" data-mp="1m9hd86r41y2h">
          <FilterIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-4990" data-mp="200w04oo52jet" />
          Filter
        </div>
        <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle modules'
      }, ...allModules.map(m => ({
        value: m,
        label: m
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4991" data-mp="1v7t489vw2xvg" />
        <Select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle groepen'
      }, {
        value: '6',
        label: 'Groep 6'
      }, {
        value: '7',
        label: 'Groep 7'
      }, {
        value: '8',
        label: 'Groep 8'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4992" data-mp="3j03o11aldwch" />
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'zero_questions',
        label: '0 vragen'
      }, {
        value: 'shortfall',
        label: 'Tekort'
      }, {
        value: 'degraded',
        label: 'Onbalans'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4993" data-mp="3suvoqdzh0zbh" />
        <div className="flex-1" data-id="element-4994" data-mp="1jn8mcbj8egtc" />
        <span className="text-[11px] text-gray-500 tabular-nums px-1" data-id="element-4995" data-mp="1eoe71jkh3adz">
          {filteredFeasibility.length} feasibility · {filteredSlices.length}{' '}
          pool-slices
        </span>
      </div>

      {/* ── 5. TWO-COLUMN DETAIL ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4996" data-mp="15oipmx0bqj5i">
        {/* Feasibility findings list */}
        <Card className="p-5" data-id="element-4997" data-mp="10pxydn31v9t7">
          <div className="flex items-center justify-between mb-3" data-id="element-4998" data-mp="10qpia0nabj3n">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4999" data-mp="300fimueezx2o">
              <ClipboardList className="w-4 h-4 text-orange-500" data-id="element-5000" data-mp="2v3p44bi9d9jd" />
              Wat ontbreekt voor een toets?
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-5001" data-mp="32lwu9czlhffm">
              Layer C
            </span>
          </div>
          {filteredFeasibility.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-5003" data-mp="3cecc8aaw640i" />} text="Geen feasibility-bevindingen op deze filter." data-id="element-5002" data-mp="3cchzhj9rxi7i" /> : <div className="space-y-1.5" data-id="element-5004" data-mp="2jovlri6u0x6x">
              {filteredFeasibility.map((finding, idx) => <FeasibilityRow key={finding.comboKey} finding={finding} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-5005" data-mp="2omxdssm8k6uj" data-mpi={idx} />)}
            </div>}
        </Card>

        {/* Capacity + Distribution slice findings */}
        <Card className="p-5" data-id="element-5006" data-mp="8xly1855oknq">
          <div className="flex items-center justify-between mb-3" data-id="element-5007" data-mp="cwbj7mwfr6hx">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5008" data-mp="cypgm2el8usn">
              <ListChecks className="w-4 h-4 text-rose-500" data-id="element-5009" data-mp="bhs69s0syxa3" />
              Pool-capaciteit & spreiding
            </h3>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-5010" data-mp="ldvcpa2oti2u">
              Layer A · B
            </span>
          </div>
          {/* Layer A/B stat tiles */}
          <div className="grid grid-cols-4 gap-1.5 mb-3" data-id="element-5011" data-mp="gh50wyxx18n8">
            <MiniStat label="Gat" count={report.capacityCounts.gap} tone="red" data-id="element-5012" data-mp="ud8s0jdht0vh" />
            <MiniStat label="Dun" count={report.capacityCounts.thin} tone="amber" data-id="element-5013" data-mp="pde0cdlw879d" />
            <MiniStat label="Cito-scheef" count={report.distributionCounts.citoConcentration} tone="orange" data-id="element-5014" data-mp="z4qiuelsxl2q" />
            <MiniStat label="DOK-smal" count={report.distributionCounts.dokCoverage} tone="rose" data-id="element-5015" data-mp="1d2otpa0wyzdh" />
          </div>
          {filteredSlices.length === 0 ? <EmptyTile icon={<CheckCircle2 className="w-6 h-6 text-emerald-400" data-id="element-5017" data-mp="1wr350tq7pu90" />} text="Geen pool-bevindingen op deze filter." data-id="element-5016" data-mp="1wquomruvs6t2" /> : <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1" data-id="element-5018" data-mp="1itehtf8oab0s">
              {filteredSlices.map((slice, idx) => <SliceRow key={slice.sliceKey} slice={slice} rank={idx + 1} onSelectQuestion={onSelectQuestion} data-id="element-5019" data-mp="190qmkoxccf46" data-mpi={idx} />)}
            </div>}
        </Card>
      </div>

      {/* ── 6. DECISION HINT FOOTER ── */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100" data-id="element-5020" data-mp="1437anvo48efe">
        <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-5021" data-mp="q3ph72zvkczp" />
        <p className="text-xs text-amber-900 leading-relaxed" data-id="element-5022" data-mp="v20ob5ysc9e8">
          <span className="font-semibold" data-id="element-5023" data-mp="v3btna7vpp7g">Aanpakvolgorde:</span> begin met{' '}
          <span className="font-semibold" data-id="element-5024" data-mp="2wry64hstzysm">0 vragen</span> (rode cellen) en{' '}
          <span className="font-semibold" data-id="element-5025" data-mp="2rz51dtxd4qgj">pool-gat</span> — die kunnen nu al
          geen toets opleveren. <em data-id="element-5026" data-mp="2nzec5unhiie4">Tekort</em> en <em data-id="element-5027" data-mp="2nyuzotglsm7h">dun</em> werken op het
          randje. <em data-id="element-5028" data-mp="2izi9pzufqz0o">Onbalans</em> en <em data-id="element-5029" data-mp="2j01j2s60sf33">cito-scheef</em> blijven functioneel
          maar minder representatief voor de hele curriculumstrook. Alle
          bevindingen zijn diagnostisch (coach/info) — nooit blocker.
        </p>
      </div>
    </div>;
}
// ─── Hero Header ──────────────────────────────────────────────────────────
function HeroHeader({
  counts
}: {
  counts: {
    ok: number;
    degraded: number;
    shortfall: number;
    zero: number;
    unknown: number;
    total: number;
    readinessPct: number;
  };
}) {
  const allReady = counts.ok === counts.total && counts.total > 0;
  const ringColor = allReady ? 'text-emerald-500' : counts.zero > 0 ? 'text-red-500' : 'text-amber-500';
  const ringBg = allReady ? 'from-emerald-50 to-white' : counts.zero > 0 ? 'from-red-50 to-white' : 'from-amber-50 to-white';
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br ${ringBg} p-6`} data-id="element-5030" data-mp="1l3mqrbhcuwkk">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-5031" data-mp="1yzi0orts0xbt">
        {/* Readiness ring */}
        <div className="relative flex-shrink-0" data-id="element-5032" data-mp="28vl2fwkputy8">
          <svg width="96" height="96" viewBox="0 0 96 96" className="rotate-[-90deg]" data-id="element-5033" data-mp="28vuv5rx5bf7m">
            <circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200" data-id="element-5034" data-mp="ehk55g4tef09" />
            <motion.circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" className={ringColor} initial={{
            pathLength: 0
          }} animate={{
            pathLength: counts.readinessPct / 100
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} style={{
            strokeDasharray: 2 * Math.PI * 42
          }} data-id="element-5035" data-mp="2lcvvocyh2vzt" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5036" data-mp="2bdetw6mcd294">
            <span className="text-2xl font-bold text-gray-900 tabular-nums leading-none" data-id="element-5037" data-mp="27g8z4bafm7uf">
              {counts.readinessPct}%
            </span>
            <span className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider" data-id="element-5038" data-mp="3iu2x9d9amulv">
              klaar
            </span>
          </div>
        </div>

        {/* Header text */}
        <div className="flex-1 min-w-0" data-id="element-5039" data-mp="3vbnfbdwcyxrk">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-5040" data-mp="3vdi4j4enxc1g">
            <Layers className="w-4 h-4 text-amber-500" data-id="element-5041" data-mp="3gf672vjgtz98" />
            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider" data-id="element-5042" data-mp="3ggr2sohino8o">
              Assessment Pool · Stage 6b
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-5043" data-mp="cyz7p1832o69">
            Toets-feasibility
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed max-w-2xl" data-id="element-5044" data-mp="hvpmll85jdq3">
            {allReady ? <>
                <span className="font-semibold text-emerald-700" data-id="element-5045" data-mp="361u99hy7pei">
                  Alle {counts.total} groep × module combinaties
                </span>{' '}
                kunnen een volledige Cito-style toets opleveren. Pool-capaciteit
                en spreiding zijn gezond.
              </> : <>
                <span className="font-semibold text-gray-900" data-id="element-5046" data-mp="2s8uu6007orjn">{counts.ok}</span>{' '}
                van{' '}
                <span className="font-semibold text-gray-900" data-id="element-5047" data-mp="2x87loy212msk">
                  {counts.total}
                </span>{' '}
                combinaties kan nu een volledige toets opleveren.{' '}
                {counts.zero > 0 && <>
                    <span className="text-red-700 font-semibold" data-id="element-5048" data-mp="2nfthzzre363r">
                      {counts.zero}
                    </span>{' '}
                    {counts.zero === 1 ? 'combinatie' : 'combinaties'} heeft
                    geen MC-vragen.{' '}
                  </>}
                {counts.degraded + counts.shortfall > 0 && <>
                    <span className="text-amber-700 font-semibold" data-id="element-5049" data-mp="24jk35a8nybip">
                      {counts.degraded + counts.shortfall}
                    </span>{' '}
                    {counts.degraded + counts.shortfall === 1 ? 'staat' : 'staan'}{' '}
                    op het randje.
                  </>}
              </>}
          </p>
        </div>
      </div>
    </motion.div>;
}
// ─── KPI Tile ─────────────────────────────────────────────────────────────
function KpiTile({
  label,
  value,
  suffix,
  color,
  icon,
  hint
}: {
  label: string;
  value: number;
  suffix?: string;
  color: 'emerald' | 'amber' | 'red' | 'indigo';
  icon: React.ReactNode;
  hint: string;
}) {
  const palette = {
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-700',
      iconBg: 'bg-emerald-100'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-700',
      iconBg: 'bg-amber-100'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-700',
      iconBg: 'bg-red-100'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
      text: 'text-indigo-700',
      iconBg: 'bg-indigo-100'
    }
  }[color];
  return <div className={`relative ${palette.bg} ${palette.border} border rounded-xl p-3 group`} title={hint} data-id="element-5050" data-mp="1aaqfqhb5mvzk">
      <div className="flex items-start justify-between mb-2" data-id="element-5051" data-mp="1f91sazsu3klh">
        <div className={`w-7 h-7 rounded-lg ${palette.iconBg} ${palette.text} flex items-center justify-center`} data-id="element-5052" data-mp="1k96bixl4mtfe">
          {icon}
        </div>
      </div>
      <p className={`text-2xl font-bold ${palette.text} tabular-nums leading-none`} data-id="element-5053" data-mp="1p66h77e2du5z">
        {value}
        {suffix && <span className="text-sm font-medium ml-1 opacity-70" data-id="element-5054" data-mp="1y02pbiae962e">{suffix}</span>}
      </p>
      <p className={`text-xs font-medium ${palette.text} mt-1.5`} data-id="element-5055" data-mp="23076zbn1g6u7">{label}</p>
    </div>;
}
// ─── Feasibility Matrix ──────────────────────────────────────────────────
function FeasibilityMatrix({
  modules,
  matrix,
  selectedCellKey,
  onSelectCell
}: {
  modules: string[];
  matrix: MatrixCell[][];
  selectedCellKey: string | null;
  onSelectCell: (key: string) => void;
}) {
  return <div className="overflow-x-auto -mx-2 px-2" data-id="element-5056" data-mp="3sh2rj6ziumlx">
      <table className="min-w-full border-separate border-spacing-1" data-id="element-5057" data-mp="3sf85fpcikog9">
        <thead data-id="element-5058" data-mp="3ngwwdgu1mhz3">
          <tr data-id="element-5059" data-mp="3nihoz0wsrqub">
            <th className="text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left pl-2 pb-1 w-16" data-id="element-5060" data-mp="3nhyiqhgieqw4">
              Groep
            </th>
            {modules.map(module => {
            const cfg = MODULE_CONFIG[module];
            const bg = cfg?.bgClass ?? 'bg-gray-400';
            return <th key={module} className="text-[10px] font-bold text-gray-600 text-center pb-1 px-0.5 min-w-[78px]" data-id="element-5061" data-mp="f4946f0io4cp">
                  <div className="flex flex-col items-center gap-1" data-id="element-5062" data-mp="5bk40gaf2cwo">
                    <div className={`w-1.5 h-1.5 rounded-full ${bg}`} data-id="element-5063" data-mp="12rqxwg3cdcib" />
                    <span className="font-mono" data-id="element-5064" data-mp="y10v9amavnrb">{module}</span>
                  </div>
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-5065" data-mp="ihg3lnqo3csl">
          {matrix.map((row, rowIdx) => {
          const group = row[0]?.group;
          return <tr key={group ?? rowIdx} data-id="element-5066" data-mp="nfhngavx3g54" data-mpi={rowIdx}>
                <td className="text-xs font-semibold text-gray-700 pl-2 pr-1 align-middle" data-id="element-5067" data-mp="8lmteb0f7vdz">
                  Groep {group}
                </td>
                {row.map((cell, idx) => {
              const key = `${cell.group}::${cell.module}`;
              const selected = key === selectedCellKey;
              return <td key={key} className="p-0" data-id="element-5068" data-mp="16aepbfzecl9u" data-mpi={idx}>
                      <motion.button type="button" onClick={() => onSelectCell(key)} initial={{
                  opacity: 0,
                  scale: 0.92
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.2,
                  delay: 0.02 * (rowIdx * modules.length + idx)
                }} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.97
                }} className={`w-full ${getCellClass(cell.status, selected)}`} aria-label={`${cell.module} groep ${cell.group} — ${getStatusLabel(cell.status)}`} data-id="element-5069" data-mp="rk7ffonggakr">
                        <CellContent cell={cell} data-id="element-5070" data-mp="2zs3vtz5ri5rt" />
                      </motion.button>
                    </td>;
            })}
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
function getCellClass(status: StatusKind, selected: boolean): string {
  const ring = selected ? 'ring-2 ring-offset-1 ring-gray-900' : '';
  if (status === 'ok') return `${ring} h-14 rounded-lg bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 transition-all flex items-center justify-center`;
  if (status === 'degraded') return `${ring} h-14 rounded-lg bg-amber-50 border border-amber-200 hover:border-amber-400 hover:bg-amber-100 transition-all flex items-center justify-center`;
  if (status === 'shortfall') return `${ring} h-14 rounded-lg bg-orange-50 border border-orange-200 hover:border-orange-400 hover:bg-orange-100 transition-all flex items-center justify-center`;
  if (status === 'zero') return `${ring} h-14 rounded-lg bg-red-50 border border-red-200 hover:border-red-400 hover:bg-red-100 transition-all flex items-center justify-center`;
  return `${ring} h-14 rounded-lg bg-gray-50 border border-gray-200 hover:border-gray-300 transition-all flex items-center justify-center`;
}
function getStatusLabel(status: StatusKind): string {
  if (status === 'ok') return 'klaar';
  if (status === 'degraded') return 'onbalans';
  if (status === 'shortfall') return 'tekort';
  if (status === 'zero') return '0 vragen';
  return 'geen data';
}
function CellContent({
  cell
}: {
  cell: MatrixCell;
}) {
  if (cell.status === 'ok') {
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-5071" data-mp="1pske810xjo51" />;
  }
  if (cell.status === 'unknown') {
    return <span className="text-gray-400 text-xs" data-id="element-5072" data-mp="1kuixhmqz80wq">—</span>;
  }
  const f = cell.finding;
  if (!f) return null;
  if (f.reason === 'zero_questions') {
    return <div className="flex flex-col items-center leading-none" data-id="element-5073" data-mp="1b4piebrml3nq">
        <XCircle className="w-3.5 h-3.5 text-red-600 mb-0.5" data-id="element-5074" data-mp="1b3egkhhqq5z9" />
        <span className="text-[10px] font-bold text-red-700" data-id="element-5075" data-mp="166xt7pid7vn8">0</span>
      </div>;
  }
  if (typeof f.achievable === 'number' && typeof f.target === 'number') {
    const color = f.reason === 'shortfall' ? 'text-orange-700' : 'text-amber-700';
    return <div className="flex flex-col items-center leading-tight" data-id="element-5076" data-mp="s5tqj738p1d2">
        <span className={`text-[11px] font-bold ${color} tabular-nums`} data-id="element-5077" data-mp="s8g69u0aan6d">
          {f.achievable}/{f.target}
        </span>
        <span className="text-[9px] text-gray-500 mt-0.5" data-id="element-5078" data-mp="2l1w0jv82f61s">
          {f.reason === 'shortfall' ? 'tekort' : 'onbalans'}
        </span>
      </div>;
  }
  return <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-5079" data-mp="2723gujuyhq3b" />;
}
// ─── Cell Detail Panel ───────────────────────────────────────────────────
function CellDetailPanel({
  cell,
  onSelectQuestion,
  onClose
}: {
  cell: MatrixCell;
  onSelectQuestion: (questionId: string) => void;
  onClose: () => void;
}) {
  const cfg = MODULE_CONFIG[cell.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const f = cell.finding;
  return <div className="rounded-xl border border-gray-200 bg-white p-4" data-id="element-5080" data-mp="1oa290s07ephr">
      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-5081" data-mp="1se05euy59m74">
        <div className="flex items-center gap-3 min-w-0" data-id="element-5082" data-mp="1edfoy5fpc2gs">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${bg}`} data-id="element-5083" data-mp="1eg1xa7af2m02" />
          <div data-id="element-5084" data-mp="1jc0oe2n4qi46">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-5085" data-mp="1jemxi6po55oi">
              Groep {cell.group} ·{' '}
              <span className="font-mono" data-id="element-5086" data-mp="zstofd4kysr9">{cell.module}</span>
            </h4>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-5087" data-mp="utqno4vmv8p3">
              {getStatusLabel(cell.status)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded transition-colors" data-id="element-5088" data-mp="14pk2xw0qlgak">
          Sluiten
        </button>
      </div>

      {cell.status === 'ok' ? <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100" data-id="element-5089" data-mp="1dq8jaxdlrsqm">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-5090" data-mp="1sewa6qci7ea8" />
          <p className="text-xs text-emerald-800" data-id="element-5091" data-mp="1nh38a9i8h6fr">
            Deze combinatie kan een volledige 30-vragen Cito-style toets
            opleveren met de gewenste tier-band verhouding (35% laag · 40%
            midden · 25% hoog).
          </p>
        </div> : cell.status === 'unknown' ? <p className="text-xs text-gray-500" data-id="element-5092" data-mp="2ysxmklzgj0lv">
          Geen pool-data beschikbaar voor deze combinatie in de huidige run.
        </p> : f ? <div className="space-y-3" data-id="element-5093" data-mp="2ouzypzlif5ts">
          {/* Target vs achievable bars */}
          {typeof f.target === 'number' && <div data-id="element-5094" data-mp="2ksmuxgqbphzn">
              <div className="flex items-center justify-between text-[11px] mb-1" data-id="element-5095" data-mp="2kt5052uyr6jp">
                <span className="text-gray-600 font-medium" data-id="element-5096" data-mp="2fvmy7wfd5rl7">
                  Bereikbaar / Doel
                </span>
                <span className="text-gray-900 font-semibold tabular-nums" data-id="element-5097" data-mp="2b2afg2xd8f0d">
                  {f.achievable ?? 0} / {f.target}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5098" data-mp="216gt5rtuh75e">
                <div className={`h-full rounded-full ${f.reason === 'zero_questions' ? 'bg-red-500' : f.reason === 'shortfall' ? 'bg-orange-500' : 'bg-amber-500'}`} style={{
            width: `${Math.min(100, (f.achievable ?? 0) / f.target * 100)}%`
          }} data-id="element-5099" data-mp="1x3u6e5c16cf2" />
              </div>
            </div>}

          {/* Per-band breakdown */}
          {f.bandAchievable && f.bandTarget && <div className="grid grid-cols-3 gap-2" data-id="element-5100" data-mp="glttlqi8pesr">
              <BandTile label="Laag" achievable={f.bandAchievable.low} target={f.bandTarget.low} isEmpty={f.emptyBands?.includes('low')} data-id="element-5101" data-mp="llolnxdr4afe" />
              <BandTile label="Midden" achievable={f.bandAchievable.mid} target={f.bandTarget.mid} isEmpty={f.emptyBands?.includes('mid')} data-id="element-5102" data-mp="3p3zok8j4eii4" />
              <BandTile label="Hoog" achievable={f.bandAchievable.high} target={f.bandTarget.high} isEmpty={f.emptyBands?.includes('high')} data-id="element-5103" data-mp="2pyfrz2p6tw8" />
            </div>}

          {/* CTA */}
          {f.issue.questionId && f.issue.questionId !== 'BATCH' && f.issue.questionId !== 'UNKNOWN' && <button onClick={() => onSelectQuestion(f.issue.questionId)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 hover:bg-amber-50 hover:border-amber-200 transition-colors group" data-id="element-5104" data-mp="8hat8lr9xews">
                <span className="text-xs text-gray-700" data-id="element-5105" data-mp="3lpx6b16x6seu">
                  Bekijk representatieve vraag in{' '}
                  <span className="font-semibold" data-id="element-5106" data-mp="3k2ekv5c3t0a">Vragen</span>
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" data-id="element-5107" data-mp="3uqljjtohooqo" />
              </button>}
        </div> : null}
    </div>;
}
function BandTile({
  label,
  achievable,
  target,
  isEmpty
}: {
  label: string;
  achievable: number;
  target: number;
  isEmpty?: boolean;
}) {
  const ratio = target > 0 ? achievable / target : 0;
  const status = isEmpty || achievable === 0 ? 'red' : ratio < 1 ? 'amber' : 'emerald';
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700'
  }[status];
  return <div className={`${palette} border rounded-lg p-2 text-center`} data-id="element-5108" data-mp="217cxjm4akx7z">
      <p className="text-[9px] font-bold uppercase tracking-wider opacity-80" data-id="element-5109" data-mp="21a8w9cnyrpxh">
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums mt-0.5" data-id="element-5110" data-mp="1ri34a2xpp8ia">
        {achievable}
        <span className="text-[10px] opacity-60" data-id="element-5111" data-mp="1rfz0rm3q6bij">/{target}</span>
      </p>
    </div>;
}
// ─── Row components ──────────────────────────────────────────────────────
function FeasibilityRow({
  finding,
  rank,
  onSelectQuestion
}: {
  finding: AssessmentPoolFeasibilityFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[finding.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const questionId = finding.issue.questionId;
  const hasQuestion = questionId && questionId !== 'BATCH' && questionId !== 'UNKNOWN';
  const tone: 'red' | 'orange' | 'amber' = finding.reason === 'zero_questions' ? 'red' : finding.reason === 'shortfall' ? 'orange' : 'amber';
  const tagLabel = finding.reason === 'zero_questions' ? '0 vragen' : finding.reason === 'shortfall' ? 'tekort' : 'onbalans';
  const toneClasses: Record<'red' | 'orange' | 'amber', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100'
  };
  return <button type="button" disabled={!hasQuestion} onClick={() => hasQuestion && onSelectQuestion(questionId)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${hasQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5112" data-mp="22r6f3jcj4bxz">
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5113" data-mp="2f90cmdn0j3v4">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5114" data-mp="2k83k04qgtm69" />
      <div className="min-w-0 flex-1" data-id="element-5115" data-mp="2o9f09jng8v1s">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5116" data-mp="2o8drxmxtimnr">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5117" data-mp="2t8i799mzpbce">
            Groep {finding.group}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5118" data-mp="2xyyn10ea991x">
            / {finding.module}
          </span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[tone]}`} data-id="element-5119" data-mp="32x9yffk87rm9">
            {tagLabel}
          </span>
        </div>
        {typeof finding.achievable === 'number' && typeof finding.target === 'number' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5120" data-mp="1ctgdlpcrcoa6">
            {finding.achievable}/{finding.target} bereikbaar
            {finding.emptyBands && finding.emptyBands.length > 0 && <>
                {' '}
                · lege banden:{' '}
                <span className="font-mono text-amber-700" data-id="element-5121" data-mp="1gvuva64oxhzv">
                  {finding.emptyBands.join(', ')}
                </span>
              </>}
          </p> : finding.reason === 'zero_questions' ? <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5122" data-mp="1qmfqrkp2i6ek">
            0 MC-vragen ·{' '}
            <span className="text-red-600 font-semibold" data-id="element-5123" data-mp="24lxlegp1odvu">
              +{finding.target ?? 30} nodig
            </span>
          </p> : null}
      </div>
      {hasQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5124" data-mp="a74qykzwdt5h" />}
    </button>;
}
function SliceRow({
  slice,
  rank,
  onSelectQuestion
}: {
  slice: AssessmentPoolSliceFinding;
  rank: number;
  onSelectQuestion: (questionId: string) => void;
}) {
  const cfg = MODULE_CONFIG[slice.module];
  const bg = cfg?.bgClass ?? 'bg-gray-400';
  const representativeQuestion = slice.issues.find(i => i.questionId)?.questionId;
  const tags: Array<{
    label: string;
    tone: 'red' | 'amber' | 'orange' | 'rose';
  }> = [];
  if (slice.capacityStatus === 'gap') tags.push({
    label: 'gat',
    tone: 'red'
  });else if (slice.capacityStatus === 'thin') tags.push({
    label: 'dun',
    tone: 'amber'
  });
  if (slice.distributionReasons.includes('cito_concentration')) tags.push({
    label: 'cito-scheef',
    tone: 'orange'
  });
  if (slice.distributionReasons.includes('dok_coverage')) tags.push({
    label: 'dok-smal',
    tone: 'rose'
  });
  const toneClasses: Record<'red' | 'amber' | 'orange' | 'rose', string> = {
    red: 'text-red-700 bg-red-50 border-red-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    orange: 'text-orange-700 bg-orange-50 border-orange-100',
    rose: 'text-rose-700 bg-rose-50 border-rose-100'
  };
  return <button type="button" disabled={!representativeQuestion} onClick={() => representativeQuestion && onSelectQuestion(representativeQuestion)} className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/40 transition-colors ${representativeQuestion ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-5125" data-mp="21kx8ns04e4i4">
      <span className="text-[10px] font-bold text-gray-400 w-5 text-center flex-shrink-0" data-id="element-5126" data-mp="1in498zopgcph">
        {rank}
      </span>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bg}`} data-id="element-5127" data-mp="1em2647ro3gi8" />
      <div className="min-w-0 flex-1" data-id="element-5128" data-mp="3d0dm91ggmgch">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5129" data-mp="3czavctj50dwk">
          <span className="font-mono text-xs font-semibold text-gray-900" data-id="element-5130" data-mp="38y8w4cp801ul">
            {slice.module}
          </span>
          <span className="font-mono text-[10px] text-gray-500" data-id="element-5131" data-mp="upy8k57fnwq5">
            / {slice.tierBand}
          </span>
          {tags.map(t => <span key={t.label} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${toneClasses[t.tone]}`} data-id="element-5132" data-mp="znhnl7bygdj5">
              {t.label}
            </span>)}
        </div>
        {typeof slice.mcEligibleCount === 'number' && typeof slice.minimumViableSize === 'number' && <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-5133" data-mp="grrgx6t2usyx">
              {slice.mcEligibleCount} MC-vragen · minimum{' '}
              {slice.minimumViableSize}
              {typeof slice.remediationNeeded === 'number' && slice.remediationNeeded > 0 && <>
                    {' '}
                    ·{' '}
                    <span className="text-red-600 font-semibold" data-id="element-5134" data-mp="216tif59bb6rb">
                      +{slice.remediationNeeded} nodig
                    </span>
                  </>}
            </p>}
      </div>
      {representativeQuestion && <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5135" data-mp="23j38e8p3zlfl" />}
    </button>;
}
// ─── Small helpers ───────────────────────────────────────────────────────
function MiniStat({
  label,
  count,
  tone
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber' | 'orange' | 'rose';
}) {
  const palette = {
    red: 'bg-red-50 border-red-100 text-red-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    orange: 'bg-orange-50 border-orange-100 text-orange-700',
    rose: 'bg-rose-50 border-rose-100 text-rose-700'
  }[tone];
  return <div className={`${palette} border rounded-lg px-1.5 py-1.5 text-center`} data-id="element-5136" data-mp="308u0k19xgwi8">
      <p className="text-base font-bold tabular-nums leading-none" data-id="element-5137" data-mp="306pt59ctlfd7">{count}</p>
      <p className="text-[9px] font-medium mt-0.5 truncate" data-id="element-5138" data-mp="358f64ujls33x">{label}</p>
    </div>;
}
function LegendDot({
  color,
  label
}: {
  color: 'emerald' | 'amber' | 'orange' | 'red' | 'gray';
  label: string;
}) {
  const cls = {
    emerald: 'bg-emerald-400',
    amber: 'bg-amber-400',
    orange: 'bg-orange-400',
    red: 'bg-red-500',
    gray: 'bg-gray-300'
  }[color];
  return <div className="flex items-center gap-1.5" data-id="element-5139" data-mp="31de0bhf32n2l">
      <div className={`w-2 h-2 rounded-full ${cls}`} data-id="element-5140" data-mp="1wxutzoiy88d" />
      <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-5141" data-mp="1vmhb85a1m42">
        {label}
      </span>
    </div>;
}
function EmptyTile({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-100" data-id="element-5142" data-mp="3oaz4ebcru0vb">
      <div className="mx-auto mb-2 w-fit" data-id="element-5143" data-mp="3ka5fbenleg0u">{icon}</div>
      <p className="text-xs text-gray-600" data-id="element-5144" data-mp="knf94z8m11a7">{text}</p>
    </div>;
}
// ─── Skeleton states ─────────────────────────────────────────────────────
function SkeletonHero() {
  return <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 animate-pulse" data-id="element-5145" data-mp="fum74j4j071t">
      <div className="flex items-center gap-6" data-id="element-5146" data-mp="2a5cdpok37dvs">
        <div className="w-24 h-24 rounded-full bg-gray-200" data-id="element-5147" data-mp="2f46vqoa8cyeu" />
        <div className="flex-1 space-y-2" data-id="element-5148" data-mp="2f5i0aqbi2a70">
          <div className="h-3 w-32 bg-gray-200 rounded" data-id="element-5149" data-mp="20fj3aqvol4t4" />
          <div className="h-5 w-48 bg-gray-200 rounded" data-id="element-5150" data-mp="20cwwwukj0vch" />
          <div className="h-3 w-full bg-gray-200 rounded" data-id="element-5151" data-mp="25cjbb7c3chmv" />
          <div className="h-3 w-3/4 bg-gray-200 rounded" data-id="element-5152" data-mp="1recjo8xqjdvn" />
        </div>
      </div>
    </div>;
}
function SkeletonMatrix() {
  return <div className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse" data-id="element-5153" data-mp="1wdxlcr4ai0c2">
      <div className="h-4 w-48 bg-gray-200 rounded mb-4" data-id="element-5154" data-mp="1hgornqrikkh6" />
      <div className="grid grid-cols-8 gap-1.5" data-id="element-5155" data-mp="1hi9l1d23dxdg">
        {Array.from({
        length: 24
      }).map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" data-id="element-5156" data-mp="3bteke565bnmz" data-mpi={i} />)}
      </div>
    </div>;
}